// Haptic and Sound Feedback Utilities
// Provides tactile and audio feedback for interactions

// Check if haptic feedback is available
const hasHapticSupport = () => {
  return 'vibrate' in navigator;
};

// Haptic feedback patterns
export const haptic = {
  // Light tap - for selections
  light: () => {
    if (hasHapticSupport()) {
      navigator.vibrate(10);
    }
  },
  
  // Medium tap - for confirmations
  medium: () => {
    if (hasHapticSupport()) {
      navigator.vibrate(25);
    }
  },
  
  // Success pattern - double tap
  success: () => {
    if (hasHapticSupport()) {
      navigator.vibrate([15, 50, 15]);
    }
  },
  
  // Error pattern - longer vibration
  error: () => {
    if (hasHapticSupport()) {
      navigator.vibrate([50, 30, 50]);
    }
  },
  
  // Warning pattern
  warning: () => {
    if (hasHapticSupport()) {
      navigator.vibrate([30, 20, 30, 20, 30]);
    }
  }
};

// Sound effects manager
class SoundManager {
  constructor() {
    this.enabled = this.loadPreference();
    this.audioContext = null;
    this.initialized = false;
  }
  
  loadPreference() {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('cipher-sounds');
    return stored !== 'false';
  }
  
  savePreference(enabled) {
    localStorage.setItem('cipher-sounds', String(enabled));
    this.enabled = enabled;
  }
  
  toggle() {
    this.savePreference(!this.enabled);
    return this.enabled;
  }
  
  isEnabled() {
    return this.enabled;
  }
  
  async init() {
    if (this.initialized || typeof window === 'undefined') return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.log('Audio not supported');
    }
  }
  
  // Generate simple tones
  async playTone(frequency, duration, type = 'sine', volume = 0.1) {
    if (!this.enabled) return;
    
    await this.init();
    if (!this.audioContext) return;
    
    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.type = type;
      oscillator.frequency.value = frequency;
      
      // Fade in and out for smoother sound
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch (e) {
      // Audio failed, ignore silently
    }
  }
  
  // Predefined sounds
  tap() {
    this.playTone(800, 0.05, 'sine', 0.05);
  }
  
  select() {
    this.playTone(600, 0.08, 'sine', 0.08);
  }
  
  success() {
    this.playTone(523, 0.1, 'sine', 0.1); // C5
    setTimeout(() => this.playTone(659, 0.1, 'sine', 0.1), 100); // E5
    setTimeout(() => this.playTone(784, 0.15, 'sine', 0.1), 200); // G5
  }
  
  error() {
    this.playTone(200, 0.2, 'square', 0.08);
  }
  
  notification() {
    this.playTone(880, 0.1, 'sine', 0.08);
    setTimeout(() => this.playTone(1047, 0.15, 'sine', 0.08), 120);
  }
  
  unlock() {
    // Rising arpeggio for unlock
    const notes = [392, 494, 587, 784]; // G4, B4, D5, G5
    notes.forEach((freq, i) => {
      setTimeout(() => this.playTone(freq, 0.12, 'sine', 0.1), i * 80);
    });
  }
}

// Singleton instance
export const sound = new SoundManager();

// Combined feedback function
export const feedback = {
  tap: () => {
    haptic.light();
    sound.tap();
  },
  
  select: () => {
    haptic.medium();
    sound.select();
  },
  
  success: () => {
    haptic.success();
    sound.success();
  },
  
  error: () => {
    haptic.error();
    sound.error();
  },
  
  unlock: () => {
    haptic.success();
    sound.unlock();
  }
};

// React hook for feedback settings
import { useState, useCallback } from 'react';

export function useFeedback() {
  const [soundEnabled, setSoundEnabled] = useState(sound.isEnabled());
  
  const toggleSound = useCallback(() => {
    const newState = sound.toggle();
    setSoundEnabled(newState);
    return newState;
  }, []);
  
  return {
    soundEnabled,
    toggleSound,
    feedback,
    haptic,
    sound
  };
}

export default feedback;
